import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { UNSAFE_NavigationContext, useBeforeUnload, useLocation, useNavigate } from "react-router"

const defaultMessage = "Du hast ungespeicherte Änderungen. Möchtest du die Seite wirklich verlassen?"

type Transition = {
    targetPath: string
    retry?: () => void
}

type BlockNavigator = {
    block?: (blocker: (transition: { retry: () => void }) => void) => () => void
}

function useUnsavedChangesWarning(when: boolean, message: string = defaultMessage) {
    const navigate = useNavigate()
    const location = useLocation()
    const navigationContext = useContext(UNSAFE_NavigationContext) as unknown as { navigator: BlockNavigator }
    const currentPathRef = useRef(`${location.pathname}${location.search}${location.hash}`)
    const allowNextNavigationRef = useRef(false)
    const [pendingTransition, setPendingTransition] = useState<Transition | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)

    useEffect(() => {
        currentPathRef.current = `${location.pathname}${location.search}${location.hash}`
    }, [location.pathname, location.search, location.hash])

    useBeforeUnload(
        (event: BeforeUnloadEvent) => {
            if (!when) return
            event.preventDefault()
            event.returnValue = message
        },
        { capture: true }
    )

    useEffect(() => {
        if (!when) {
            return
        }

        const blockerApi = navigationContext.navigator?.block

        if (typeof blockerApi === "function") {
            const unblock = blockerApi((transition: { retry: () => void }) => {
                setPendingTransition({ targetPath: "", retry: transition.retry })
                setIsModalOpen(true)
            })

            return unblock
        }

        const originalPushState = window.history.pushState.bind(window.history)
        const originalReplaceState = window.history.replaceState.bind(window.history)

        const getPathFromUrl = (url?: string | URL | null) => {
            if (!url) {
                return currentPathRef.current
            }

            const resolvedUrl = new URL(url.toString(), window.location.origin)
            return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`
        }

        const shouldBypassGuard = () => {
            if (!allowNextNavigationRef.current) {
                return false
            }

            allowNextNavigationRef.current = false
            return true
        }

        window.history.pushState = function (state: any, title: string, url?: string | URL | null) {
            if (shouldBypassGuard()) {
                return originalPushState(state, title, url)
            }

            const targetPath = getPathFromUrl(url)
            if (targetPath !== currentPathRef.current) {
                setPendingTransition({ targetPath })
                setIsModalOpen(true)
                return
            }

            return originalPushState(state, title, url)
        }

        window.history.replaceState = function (state: any, title: string, url?: string | URL | null) {
            if (shouldBypassGuard()) {
                return originalReplaceState(state, title, url)
            }

            const targetPath = getPathFromUrl(url)
            if (targetPath !== currentPathRef.current) {
                setPendingTransition({ targetPath })
                setIsModalOpen(true)
                return
            }

            return originalReplaceState(state, title, url)
        }

        const onPopState = () => {
            if (allowNextNavigationRef.current) {
                allowNextNavigationRef.current = false
                return
            }

            const currentPath = currentPathRef.current
            const targetPath = `${window.location.pathname}${window.location.search}${window.location.hash}`

            if (targetPath === currentPath) {
                return
            }

            originalPushState(null, "", currentPath)
            setPendingTransition({ targetPath })
            setIsModalOpen(true)
        }

        window.addEventListener("popstate", onPopState)
        return () => {
            window.history.pushState = originalPushState
            window.history.replaceState = originalReplaceState
            window.removeEventListener("popstate", onPopState)
        }
    }, [navigationContext.navigator, when])

    useEffect(() => {
        if (when) {
            return
        }

        setPendingTransition(null)
        setIsModalOpen(false)
    }, [when])

    const confirmNavigation = useCallback(() => {
        const transition = pendingTransition
        setIsModalOpen(false)
        setPendingTransition(null)

        if (!transition) {
            return
        }

        if (transition.retry) {
            transition.retry()
            return
        }

        allowNextNavigationRef.current = true
        navigate(transition.targetPath)
    }, [navigate, pendingTransition])

    const cancelNavigation = useCallback(() => {
        setPendingTransition(null)
        setIsModalOpen(false)
    }, [])

    return {
        isUnsavedChangesModalOpen: isModalOpen,
        confirmNavigation,
        cancelNavigation,
    }
}

export default useUnsavedChangesWarning